import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {BaseurlService} from '../../services/baseurl.service';
import {Observable} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class InsoDockerService {

  constructor(
    private http: HttpClient,
    private baseurlService: BaseurlService,
  ) { }

  // ── what is running out there ──────────────────────────────────────────────

  getOverview(): Observable<InsoDockerOverview> {
    return this.http.get<InsoDockerOverview>(`${this.base()}/overview`);
  }

  getSuggestion(image: string, tenant: string | null, vm: string | null): Observable<InsoDockerUpdateSuggestion> {
    let params = new HttpParams().set('image', image);
    if (tenant) {
      params = params.set('tenant', tenant);
    }
    if (vm) {
      params = params.set('vm', vm);
    }
    return this.http.get<InsoDockerUpdateSuggestion>(`${this.base()}/suggestion`, {params});
  }

  /** asks the registries right now what the newest versions are, answers when they are all looked up */
  refreshUpstream(): Observable<InsoDockerRefreshResult> {
    return this.http.post<InsoDockerRefreshResult>(`${this.base()}/refreshUpstream`, null);
  }

  /** pulls the image of a row on its vm, fire and forget, the answer is only the handle of the stracli command */
  pull(request: InsoDockerPullRequest): Observable<InsoDockerPullResult> {
    return this.http.post<InsoDockerPullResult>(`${this.base()}/pull`, request);
  }

  // ── rules ──────────────────────────────────────────────────────────────────

  getRules(): Observable<InsoDockerRule[]> {
    return this.http.get<InsoDockerRule[]>(`${this.base()}/rules`);
  }

  createRule(rule: InsoDockerRule): Observable<InsoDockerRule> {
    return this.http.post<InsoDockerRule>(`${this.base()}/rules`, rule);
  }

  updateRule(rule: InsoDockerRule): Observable<InsoDockerRule> {
    return this.http.put<InsoDockerRule>(`${this.base()}/rules/${rule.id}`, rule);
  }

  deleteRule(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base()}/rules/${id}`);
  }

  // ── patch days ─────────────────────────────────────────────────────────────

  getPatchDays(): Observable<InsoDockerPatchDay[]> {
    return this.http.get<InsoDockerPatchDay[]>(`${this.base()}/patchDays`);
  }

  getPatchDay(id: number): Observable<InsoDockerPatchDayDetail> {
    return this.http.get<InsoDockerPatchDayDetail>(`${this.base()}/patchDays/${id}`);
  }

  createPatchDay(patchDay: InsoDockerPatchDay): Observable<InsoDockerPatchDay> {
    return this.http.post<InsoDockerPatchDay>(`${this.base()}/patchDays`, patchDay);
  }

  updatePatchDay(patchDay: InsoDockerPatchDay): Observable<InsoDockerPatchDay> {
    return this.http.put<InsoDockerPatchDay>(`${this.base()}/patchDays/${patchDay.id}`, patchDay);
  }

  deletePatchDay(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base()}/patchDays/${id}`);
  }

  // ── the planned version updates of a patch day ─────────────────────────────

  createItem(patchDayId: number, item: InsoDockerPatchDayItem): Observable<InsoDockerPatchDayItem> {
    return this.http.post<InsoDockerPatchDayItem>(`${this.base()}/patchDays/${patchDayId}/items`, item);
  }

  updateItem(item: InsoDockerPatchDayItem): Observable<InsoDockerPatchDayItem> {
    return this.http.put<InsoDockerPatchDayItem>(`${this.base()}/items/${item.id}`, item);
  }

  /** moves planned updates to another patch day, what one patch day did not get done is the work of the next one */
  moveItems(patchDayId: number, itemIds: number[]): Observable<InsoDockerPatchDayItem[]> {
    return this.http.post<InsoDockerPatchDayItem[]>(`${this.base()}/patchDays/${patchDayId}/items/move`, {itemIds});
  }

  deleteItem(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base()}/items/${id}`);
  }

  private base(): string {
    return `${this.baseurlService.getBaseUrl()}/insoDocker`;
  }
}

export type InsoDockerStatus = 'CRITICAL' | 'OUTDATED' | 'UP_TO_DATE' | 'LATEST' | 'UNKNOWN' | 'IGNORED';

export type InsoDockerUpstreamSource = 'GITHUB' | 'DOCKERHUB' | 'GHCR' | 'GITLAB' | 'REGISTRY_V2' | 'MANUAL' | 'INTERNAL' | 'UNKNOWN';

export type InsoDockerRuleType = 'CRITICAL_BELOW' | 'ABANDONED' | 'IGNORE' | 'LATEST_OVERRIDE';

export type InsoDockerPatchDayItemStatus = 'PLANNED' | 'DONE' | 'SKIPPED';

export interface InsoDockerOverview {
  generated: string;
  lastScan: string | null;
  rows: InsoDockerOverviewRow[];
}

export interface InsoDockerOverviewRow {
  tenant: string;
  vm: string;
  vmPrefix: string;
  image: string;
  tag: string;
  containers: string[];
  containerCount: number;
  /** the directories of the docker-compose files, empty when nothing here was started by compose */
  composeDirs: string[];
  latestVersion: string | null;
  source: InsoDockerUpstreamSource;
  status: InsoDockerStatus;
  criticalBelow: string | null;
  /** the image itself is dead, not just this version of it */
  abandoned: boolean;
  planned: boolean;
  plannedToVersion: string | null;
  patchDayId: number | null;
  patchDayItemId: number | null;
  firstSeen: string;
  lastSeen: string;
  /** false while the vm of this version does not answer, then this is the last thing we know and not a fact */
  reporting: boolean;
}

export interface InsoDockerUpdateSuggestion {
  image: string;
  tenant: string | null;
  vm: string | null;
  fromVersions: string[];
  toVersion: string | null;
}

/** the one image on the one vm that should be pulled */
export interface InsoDockerPullRequest {
  tenant: string;
  vm: string;
  image: string;
  tag: string;
}

/** uid being the stracli command, look it up under the stracli command responses of host to see what it did */
export interface InsoDockerPullResult {
  uid: string;
  host: string;
}

/** changed being the number of images that got a new newest version out of the lookups */
export interface InsoDockerRefreshResult {
  changed: number;
}

export interface InsoDockerRule {
  id: number | null;
  image: string;
  type: InsoDockerRuleType;
  value: string | null;
  comment: string | null;
  active: boolean;
  created?: string | null;
  createdBy?: string | null;
}

export interface InsoDockerPatchDay {
  id: number | null;
  day: string | null;
  title: string | null;
  closed: boolean;
  closedAt?: string | null;
  created?: string | null;
  plannedCount: number;
  doneCount: number;
  skippedCount: number;
}

export interface InsoDockerPatchDayDetail {
  patchDay: InsoDockerPatchDay;
  items: InsoDockerPatchDayItem[];
}

export interface InsoDockerPatchDayItem {
  id: number | null;
  patchDayId?: number | null;
  image: string;
  tenant: string | null;
  vm: string | null;
  fromVersion: string | null;
  toVersion: string | null;
  status: InsoDockerPatchDayItemStatus;
  comment: string | null;
  created?: string | null;
  updated?: string | null;
  doneAt?: string | null;
  currentVersions: string[];
  vms: string[];
  /** the vms of this item that do not answer right now, their versions are the last ones we saw */
  downVms: string[];
}
